import { Container, type Ticker } from 'pixi.js';
import type { GameState } from '../app/GameState';
import type { ViewportLayout } from '../app/Viewport';
import type { AssetLoader } from '../systems/AssetLoader';
import type { AudioManager } from '../systems/AudioManager';
import type { InputManager } from '../systems/InputManager';
import type { SaveManager } from '../systems/SaveManager';
import type { TextLayoutDebugState } from '../ui/TextLayoutDebug';

export interface SceneServices {
  getLayout: () => ViewportLayout;
  getState: () => GameState;
  getInput: () => InputManager;
  getAssets: () => AssetLoader;
  getSaveManager: () => SaveManager;
  getAudio: () => AudioManager;
}

export abstract class BaseScene {
  public readonly container = new Container();
  private readonly cleanupCallbacks: Array<() => void> = [];
  private started = false;

  protected constructor(protected readonly services: SceneServices) {}

  public start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.onStart();
  }

  public update(_ticker: Ticker): void {}

  public resize(_layout: ViewportLayout): void {}

  public getTextLayoutDebugState(): TextLayoutDebugState {
    return {
      scene: 'unknown',
      entries: []
    };
  }

  public destroy(): void {
    for (const cleanup of this.cleanupCallbacks.splice(0).reverse()) {
      cleanup();
    }

    this.onDestroy();
    this.container.destroy({ children: true });
    this.started = false;
  }

  protected onStart(): void {}

  protected onDestroy(): void {}

  protected addDomListener<K extends keyof WindowEventMap>(
    target: Window,
    type: K,
    listener: (event: WindowEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): void;

  protected addDomListener<K extends keyof DocumentEventMap>(
    target: Document,
    type: K,
    listener: (event: DocumentEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): void;

  protected addDomListener<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): void;

  protected addDomListener(
    target: Window | Document | HTMLElement,
    type: string,
    listener: EventListener,
    options?: AddEventListenerOptions
  ): void {
    target.addEventListener(type, listener, options);
    this.cleanupCallbacks.push(() => target.removeEventListener(type, listener, options));
  }

  protected addTicker(ticker: Ticker, callback: (ticker: Ticker) => void): void {
    ticker.add(callback);
    this.cleanupCallbacks.push(() => ticker.remove(callback));
  }

  protected addCleanup(cleanup: () => void): void {
    this.cleanupCallbacks.push(cleanup);
  }
}
